import { BittsTooltip } from '@crossbeam/bitts';

import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

import useAuth from '@/composables/useAuth';
import { DISABLED_LISTS, NO_ACCESS } from '@/constants/shared_list';
import { useCollaborateStore, useFeedsStore, useSourcesStore } from '@/stores';

type OptionsType = {
  sourceId: MaybeRefOrGetter<number | string>;
  sourcePrimaryKey: MaybeRefOrGetter<string>;
  isCompanyRecord: MaybeRefOrGetter<boolean>;
};

export default function useRecordHeaderActions({
  sourceId,
  sourcePrimaryKey,
  isCompanyRecord,
}: OptionsType) {
  const { hasScope } = useAuth();
  const collaborateStore = useCollaborateStore();
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();

  const source = computed(() =>
    sourcesStore.getSourceById(Number(toValue(sourceId))),
  );

  const feed = computed(() =>
    source.value?.feed_id ? feedsStore.getFeedById(source.value.feed_id) : null,
  );

  const isSalesforce = computed(
    () => !!source.value?.schema?.includes('salesforce'),
  );
  const isMicrosoftDynamics = computed(
    () => !!source.value?.schema?.includes('dynamics'),
  );

  const recordLinkUrl = computed(() => {
    const baseUrl = feed.value?.external_base_url ?? null;
    if (isSalesforce.value) return `${baseUrl}/${toValue(sourcePrimaryKey)}`;
    if (isMicrosoftDynamics.value)
      return `${baseUrl}/main.aspx?pagetype=entityrecord&etn=${source.value?.mdm_type}&id=${toValue(sourcePrimaryKey)}`;
    return null;
  });

  const viewInCrmText = computed(
    () => `View in ${isSalesforce.value ? 'Salesforce' : 'Microsoft Dynamics'}`,
  );

  const hasListAccess = computed(
    () =>
      collaborateStore.listAccess !== NO_ACCESS &&
      collaborateStore.listAccess !== DISABLED_LISTS,
  );

  const canShareWithPartner = computed(
    () => toValue(isCompanyRecord) && hasScope('write:lists'),
  );

  const shareTooltipOrDiv = computed(() =>
    hasListAccess.value ? 'div' : BittsTooltip,
  );

  const shareTooltipProps = computed(() => {
    if (hasListAccess.value) return {};
    return {
      placement: 'bottomLeft',
      includeCta: true,
      ctaIcon: ['fak', 'upgrade'],
      buttonText: 'Upgrade',
    };
  });

  return {
    canShareWithPartner,
    hasListAccess,
    recordLinkUrl,
    shareTooltipOrDiv,
    shareTooltipProps,
    viewInCrmText,
  };
}
